import { useEffect, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLevelStore } from '../store/levelStore';
import { FiStar, FiLock, FiArrowLeft, FiChevronDown } from 'react-icons/fi';

const LevelMapPage = () => {
  const { levels, progress, totalLevels, hasMore, isLoading, fetchLevels, fetchProgress, loadMoreLevels } = useLevelStore();
  const loaderRef = useRef<HTMLDivElement>(null);
  const [searchNum, setSearchNum] = useState('');

  // Initial load
  useEffect(() => {
    fetchLevels(1);
    fetchProgress();
  }, []);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreLevels();
        }
      },
      { rootMargin: '400px' }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMoreLevels]);

  const getProgressForLevel = (levelNumber: number) => {
    return progress.find(p => p.levelNumber === levelNumber);
  };

  const isLevelUnlocked = (levelNumber: number) => {
    if (levelNumber === 1) return true;
    const prevProgress = progress.find(p => p.levelNumber === levelNumber - 1);
    return prevProgress?.completed || false;
  };

  const renderStars = (stars: number) => (
    <div className="stars">
      {[1, 2, 3].map(i => (
        <FiStar key={i} className={i <= stars ? 'filled' : ''} />
      ))}
    </div>
  );

  const handleSearch = () => {
    const num = parseInt(searchNum);
    if (num >= 1 && num <= totalLevels) {
      window.location.href = `/game/${num}`;
    }
  };

  return (
    <div className="level-map-page">
      {/* Header */}
      <header className="page-header">
        <div className="header-left">
          <Link to="/" className="btn btn-secondary btn-icon">
            <FiArrowLeft />
          </Link>
          <div>
            <h1 className="page-title">Карта уровней</h1>
            <span className="page-subtitle">{totalLevels.toLocaleString()} уровней</span>
          </div>
        </div>

        {/* Jump to level */}
        <div className="search-box">
          <input
            type="number"
            min={1}
            max={totalLevels}
            placeholder="Перейти к..."
            value={searchNum}
            onChange={e => setSearchNum(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="btn btn-small">Go</button>
        </div>
      </header>

      {/* Levels list */}
      <main className="level-list-container">
        <div className="level-path">
          {levels.map((level, index) => {
            const isUnlocked = isLevelUnlocked(level.levelNumber);
            const prog = getProgressForLevel(level.levelNumber);
            const isCompleted = prog?.completed || false;

            return (
              <div
                key={level._id}
                className={`level-node ${isUnlocked ? 'unlocked' : 'locked'} ${isCompleted ? 'completed' : ''}`}
              >
                {isUnlocked ? (
                  <Link to={`/game/${level.levelNumber}`} className="level-card card">
                    <div className="level-number">{level.levelNumber}</div>
                    <div className="level-details">
                      <h3>{level.name}</h3>
                      <p>{level.gridSize.width}×{level.gridSize.height} · {level.moveLimit} ходов</p>
                      {prog && (
                        <div className="level-progress-info">
                          {renderStars(prog.stars)}
                          <span className="best-score">Лучший: {prog.bestScore}</span>
                        </div>
                      )}
                    </div>
                    {!isCompleted && <div className="play-badge">ИГРАТЬ</div>}
                  </Link>
                ) : (
                  <div className="level-card locked-card">
                    <FiLock className="lock-icon" />
                    <div className="level-details">
                      <h3>Уровень {level.levelNumber}</h3>
                      <p>Пройдите предыдущий уровень, чтобы разблокировать</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Infinite scroll loader */}
        {hasMore && (
          <div ref={loaderRef} className="load-more-area">
            {isLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>Загрузка уровней...</span>
              </div>
            ) : (
              <div className="load-more-hint">
                <FiChevronDown size={24} />
                <span>Ещё уровни</span>
              </div>
            )}
          </div>
        )}
      </main>

      <style>{`
        .level-map-page { min-height: 100vh; background: #0a0a1a; }

        .page-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px;
          background: rgba(18,18,48,0.9); backdrop-filter: blur(10px);
          border-bottom: 1px solid #2a2a5a;
          position: sticky; top: 0; z-index: 100;
        }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .page-title { font-size: 1.25rem; font-weight: 700; color: #fff; margin: 0; }
        .page-subtitle { font-size: 0.75rem; color: #8888bb; }

        .search-box { display: flex; gap: 6px; align-items: center; }
        .search-box input {
          width: 80px; padding: 6px 10px; border: 1px solid #2a2a5a;
          border-radius: 8px; background: #12123a; color: #fff;
          font-size: 0.875rem; text-align: center;
        }
        .btn-small {
          padding: 6px 14px; font-size: 0.8rem; border-radius: 8px;
          background: linear-gradient(135deg, #3742fa, #00d4ff);
          border: none; color: #fff; font-weight: 600; cursor: pointer;
        }

        .level-list-container { padding-bottom: 60px; }
        .level-path { display: flex; flex-direction: column; gap: 8px; max-width: 500px; margin: 16px auto; padding: 0 12px; }

        .level-card {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 16px; text-decoration: none; color: inherit;
          background: rgba(18,18,48,0.7); backdrop-filter: blur(8px);
          border: 1px solid #2a2a5a; border-radius: 12px;
          transition: all 0.2s;
        }
        .level-card:hover:not(.locked-card) {
          transform: translateX(4px); border-color: #3742fa;
          box-shadow: 0 4px 20px rgba(55,66,250,0.15);
        }
        .locked-card { opacity: 0.4; cursor: not-allowed; }

        .level-number {
          width: 48px; height: 48px; display: flex; align-items: center;
          justify-content: center; background: linear-gradient(135deg, #3742fa, #00d4ff);
          border-radius: 10px; font-size: 1.1rem; font-weight: 700; flex-shrink: 0;
        }
        .completed .level-number { background: linear-gradient(135deg, #2ed573, #7bed9f); }

        .level-details { flex: 1; min-width: 0; }
        .level-details h3 { font-size: 0.95rem; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .level-details p { color: #8888bb; font-size: 0.75rem; margin: 0; }

        .stars { display: flex; gap: 2px; margin-top: 4px; }
        .stars svg { width: 14px; height: 14px; color: #555577; }
        .stars .filled { color: #ffaa00; }
        .level-progress-info { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
        .best-score { font-size: 0.7rem; color: #666699; }

        .play-badge {
          padding: 3px 10px; background: #2ed573; color: #0a0a1a;
          font-size: 0.65rem; font-weight: 700; border-radius: 12px;
          flex-shrink: 0;
        }

        .lock-icon { width: 48px; height: 48px; color: #555577; flex-shrink: 0; }

        .load-more-area { text-align: center; padding: 20px; }
        .loading-spinner { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #8888bb; }
        .spinner {
          width: 30px; height: 30px; border: 3px solid #2a2a5a;
          border-top-color: #3742fa; border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .load-more-hint { display: flex; flex-direction: column; align-items: center; gap: 4px; color: #555577; font-size: 0.8rem; }

        @media (max-width: 480px) {
          .page-header { flex-direction: column; gap: 8px; }
          .search-box { width: 100%; }
          .search-box input { flex: 1; }
        }
      `}</style>
    </div>
  );
};

export default LevelMapPage;
